import { pack } from '@attain-apps/data-utils'
import * as errors from '@attain-apps/errors'
import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import * as models from '../../../../models'
import { Value } from '../../../value'
import * as node from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'

import { Add } from './add'
import { Equals } from './equals'
import { GetAttribute } from './get-attribute'
import { If } from './if'

/**
 * Gets the numerical score associated with an attribute (and / or value) for the current querying model(id).
 *
 * By providing different {@link Score.Args} the {@link Score} function will have different
 * {@link Score.Behavior}
 *
 * @param {Score.Args} value - Arguments passed to score function:
 * @example
 * // Given scoring configuration:
 * // { id: '!type', lookup: { 'foo': 10, 'bar': 20 }, default: 0 }
 * // { id: '!active', lookup: { 'false': 0, 'true': 5 }, default: 0 }
 * // { id: '!status', lookup: { 'open': 5, 'closed': 15 }, default: 0 }
 *
 * // Get score for single attribute where value='foo'
 * { 'score': '!type' }  // -> 10
 *
 * // Get sum of scores where type='bar' and active='true'
 * { 'score': ['!type', '!active'] }  // -> 25
 *
 * // Get sum of all configured scores where type='foo', active='true', status='closed'
 * { 'score': '*' }  // -> 30
 *
 * // When attribute value doesn't match any lookup value status='inprogress', returns default
 * { 'score': '!status' }  // -> 0
 *
 * // Get maximum possible score for all configured attributes
 * { 'score': { '+possible': '*' } }  // -> 40
 * // or
 * { 'score': { 'possible': '*' } }  // -> 40
 *
 * // Get maximum possible score for provided attributes
 * { 'score': { '+possible': ['!type', '!active'] } }  // -> 25
 * // or
 * { 'score': { 'possible': ['!type', '!active'] } }  // -> 25
 *
 * // Get maximum possible score for single attribute
 * { 'score': { '+possible': '!type' } }  // -> 20
 * // or
 * { 'score': { 'possible': '!type' } }  // -> 20
 *
 * // Get minimum possible score for all configured attributes
 * { 'score': { '-possible': '*' } }  // -> 15
 *
 * // Get minimum possible score for provided attributes
 * { 'score': { '-possible': ['!type', '!active'] } }  // -> 10
 *
 * // Get minimum possible score for single attribute
 * { 'score': { '-possible': '!type' } }  // -> 10
 *
 * // Get distributed score for all configured attributes
 * // with values type='foo'; active ='true'; status='closed'
 * { 'score': { '...': '*' } }  // -> [ 10, 5, 15 ]
 *
 * // Get distributed score for provided attributes
 * // with values type='foo'; active ='true'; status='closed'
 * { 'score': { '...': ['!type', '!active'] } }  // -> [ 10, 5 ]
 *
 * // Get distributed score for single attribute
 * // with values type='foo'; active ='true'; status='closed'
 * { 'score': { '...': '!type' } }  // -> [ 10 ]
 */
export interface Score<out T = any> {
  /** {@link Score} */
  readonly [Key.Score]: Score.Args
}
export namespace Score {
  export const isScore: TypeChecker<Score> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Score>({
      [Key.Score]: PartialId.isPartialId,
    }),
  ).usingAlias('Fn.Score')
  export const assertIsScore: TypeAsserter<Score> =
    TypeAsserter.Factory.from(isScore)
  /**
   * Arguments for the {@link Score} function.
   */
  export type Args =
    | AttributeSelection
    | ExactlyOneOf<{
        [Score.Behavior.ImplicitMaxPossibleScore]: Score.AttributeSelection
        [Score.Behavior.MaxPossibleScore]: Score.AttributeSelection
        [Score.Behavior.MinPossibleScore]: Score.AttributeSelection
        [Score.Behavior.Distributed]: Score.AttributeSelection
      }>
  export namespace Args {
    /**
     * A normalized version of {@link Args} with stricter typing to facilitate
     * processing.
     */
    export interface Normalized {
      /**
       * Similar to {@link Behavior} but excluding
       * {@link Behavior.ImplicitMaxPossibleScore} since it is same as
       * {@link Behavior.MaxPossibleScore} but without the `+` prefix.
       */
      scoringBehavior: Exclude<Behavior, Behavior.ImplicitMaxPossibleScore>
      /**
       * List of attribute IDs to be used for scoring.
       */
      attributePartialIds: readonly PartialId[]
    }

    /**
     * Converts {@link Args} to {@link Normalized} for easier processing.
     */
    export function toNormalized(
      args: Args,
      { schema, modelId }: Required<Pick<node.Context, 'schema' | 'modelId'>>,
    ): Normalized {
      /** Helper function to pack the attribute selection for easier processing */
      function _packAttributeSelection(selection: AttributeSelection) {
        return selection === '*'
          ? // if its '*' we need to get all attributeIds for the model
            schema
              .getAttributesOf(modelId)
              .map(attribute => models.Id.Parts.from(attribute.id).partialId)
          : // ensure consistent array format by wrapping single attributeId in an array
            pack(selection)
      }

      if (AttributeSelection.isTargetAttributes(args)) {
        return {
          attributePartialIds: _packAttributeSelection(args),
          scoringBehavior: Score.Behavior.Basic,
        }
      } else if (TypeChecker.isObject(args)) {
        // To provide flexibility, we check both prefixed (+possible) and
        // prefix-less (possible) versions, treating them the same way in
        // subsequent processing
        const maxPossibleScoreArgs =
          args[Score.Behavior.MaxPossibleScore] ??
          args[Score.Behavior.ImplicitMaxPossibleScore]
        const minPossibleScoreArgs = args[Score.Behavior.MinPossibleScore]
        const distributedArgs = args[Score.Behavior.Distributed]

        if (maxPossibleScoreArgs) {
          return {
            attributePartialIds: _packAttributeSelection(maxPossibleScoreArgs),
            scoringBehavior: Score.Behavior.MaxPossibleScore,
          }
        }
        if (minPossibleScoreArgs) {
          return {
            attributePartialIds: _packAttributeSelection(minPossibleScoreArgs),
            scoringBehavior: Score.Behavior.MinPossibleScore,
          }
        }
        if (distributedArgs) {
          return {
            attributePartialIds: _packAttributeSelection(distributedArgs),
            scoringBehavior: Score.Behavior.Distributed,
          }
        }

        // TODO: Is there a way to evaluate this statically?
        // Probably not, since we can pass what ever we want as DMS query?
        throw new Error('Not correct scoring arguments')
      } else {
        // TODO: how to handle this?
        throw new Error('Not correct scoring arguments')
      }
    }
  }

  /**
   * Defines a flexible way to select attributes for the {@link Score} function.
   *
   * Accepts:
   * - `'*'` — All attributes
   * - `PartialId[]` — A list of specific attribute IDs
   * - `PartialId` — A single attribute ID
   */
  export type AttributeSelection = PartialId | readonly PartialId[] | '*'
  export namespace AttributeSelection {
    export const isTargetAttributes: TypeChecker<AttributeSelection> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          PartialId.isPartialId,
          TypeChecker.Factory.list(PartialId.isPartialId),
          TypeChecker.Factory.exact('*'),
        ),
      )
  }

  /**
   * Represents different behaviors for scoring in the model. Each _"behavior"_
   * determines how scores are calculated
   */
  export enum Behavior {
    /**
     * Returns the sum of all scores for the `model` based on the
     * attributes' __current__ values and their configured scores.
     */
    Basic = 'basic',
    /**
     * Identical to {@link MaxPossibleScore}, with no implicit `+` prefix passed
     */
    ImplicitMaxPossibleScore = 'possible',
    /**
     * Returns the maximum possible score for the `model` based on the
     * attributes and their configured scores.
     *
     * Opposite of {@link MinPossibleScore}
     */
    MaxPossibleScore = '+possible',
    /**
     * Returns the minimum possible score for the `model` based on the
     * attributes and their configured scores.
     *
     * Opposite of {@link ImplicitMaxPossibleScore}
     */
    MinPossibleScore = '-possible',
    /**
     * Returns a list of scores for the `model` based on the attributes'
     * __current__ values and their configured scores.
     */
    Distributed = '...',
  }

  export const HELPER: FnHelper<Score> = {
    createNode: (fn, context) => {
      const arg = fn[Key.Score]
      const { modelId, schema } = context

      // The scoring function requires a valid modelId to identify attributes
      // Throw an error immediately if modelId is missing
      TypeAsserter.assertIsNotNullish(
        modelId,
        `Can't use scoring function if target is not defined for given query`,
      )

      // Normalize the argument for easier processing
      const { attributePartialIds, scoringBehavior } = Score.Args.toNormalized(
        arg,
        { modelId, schema },
      )

      /**
       * List of all requested attributes process.
       *
       * @see {@link models.Attribute.Scoring}
       */
      const attributes = attributePartialIds.map(partialId =>
        // get attribute for given partial id
        schema.getAttribute(
          // convert partial id to full attribute id
          models.Attribute.Id.from({
            context: models.Id.Separator.swap(
              modelId,
              models.Id.Separator.BetweenSections,
              models.Id.Separator.WithinSection,
            ),
            partialId,
          }),
        ),
      )

      /**
       * Attributes that have {@link models.Attribute.scoring} defined
       */
      // filter out attributes without scoring configuration since they won't
      // contribute to the score
      const scorableAttributes = attributes.filter(
        // Use type guard to filter out attributes without scoring, ensuring
        // proper typing
        (
          attr,
        ): attr is typeof attr & {
          scoring: NonNullable<typeof attr.scoring>
        } => !!attr.scoring,
      )

      // Different scoring modes are handled differently
      switch (scoringBehavior) {
        case Score.Behavior.Basic: {
          /**
           * Partial score for attributes with static default score
           */
          let partialScore = 0
          /**
           * List of {@link node.Node} that will be used to calculate the score
           */
          const scoreNodes: node.Node[] = []

          for (const {
            scoring: { default: defaultScore, lookup },
            id,
            type,
          } of scorableAttributes) {
            // Start by eliminating cases where we can use a static default score -
            // when no lookup is defined, or when all values in the lookup equal the
            // default score. In these cases, we can avoid creating dynamic nodes
            // and just use the default
            if (
              lookup === undefined ||
              Object.values(lookup).every(score => score === defaultScore)
            ) {
              partialScore += defaultScore

              // continue to next attribute
              continue
            }

            // Continue by iterating over lookup to create if/else (case/when) nodes
            // for each possible value in the lookup.

            // get attribute partial id, we can do that statically, from full id
            const { partialId } = models.Id.Parts.from(id)
            /** @see {@link common.value.types.Type} */
            const attributeCommonType = Value.Type.toCommonValueType(type)

            scoreNodes.push(
              new If.Node({
                cases: Object.entries(lookup).map(([key, score]) => ({
                  then: new node.ForValue(score),
                  when: new Equals.Node([
                    new node.ForValue(
                      common.value.Raw.cast(key, attributeCommonType),
                    ),
                    new GetAttribute.Node(
                      { attributePartialId: partialId },
                      attributeCommonType,
                    ),
                  ]),
                })),
                else: new node.ForValue(defaultScore),
              }),
            )
          }

          // if we have any static default scores, add them together with the
          // dynamic nodes
          if (partialScore !== 0) {
            scoreNodes.push(new node.ForValue(partialScore))
          }

          switch (scoreNodes.length) {
            case 0:
              // No score nodes to process, return 0 rather than null since null
              // would create ambiguity
              return new node.ForValue(0)
            case 1:
              // if we have only one scoreNode, we don't need to sum
              return scoreNodes[0]
            default:
              // if we have multiple nodes, we need to add them together to get the
              // final score
              return new Add.Node(scoreNodes)
          }
        }
        // Both the UpperBound and LowerBound cases share identical logic,
        // differing only in their choice of math function (max vs min)
        case Score.Behavior.MaxPossibleScore:
        case Score.Behavior.MinPossibleScore: {
          // Return a node.ForValue since the calculation only depends on
          // attribute configuration, not on the current values stored in the
          // database.
          return new node.ForValue(
            // Sum the max/min possible scores for each configured attribute
            scorableAttributes.reduce(
              (sum, { scoring: { default: defaultScore, lookup } }) => {
                // TODO: how should we handle "defaultScore" or should we just
                // ignore it?

                // skip attributes without lookup configuration
                if (!lookup) {
                  return sum
                }

                const availableScoreValues = Object.values(lookup).filter(
                  // NOTE: While TypeScript sees this as Partial<Lookup> with possible undefined values,
                  // this case cannot occur in practice. We keep the filter as a safeguard with minimal
                  // performance impact
                  TypeChecker.isNotNullish.raw,
                )

                return (
                  sum +
                  // add highest or lowest possible attribute score to running
                  // total
                  Math[
                    // Determine which Math function should be applied based on
                    // scoring behavior
                    scoringBehavior === Score.Behavior.MaxPossibleScore
                      ? 'max'
                      : 'min'
                  ](...availableScoreValues)
                )
              },
              0,
            ),
          )
        }
        case Score.Behavior.Distributed: {
          // TODO: implement '...' scoring
          // - consider combining with Score.Behavior.Basic case and using
          //   CreateTuple.Node for implementation
          throw new errors.NotImplementedError({
            description: `Scoring with "${Score.Behavior.Distributed}" behavior `,
          })
        }
        default: {
          TypeAsserter.assertIsNever(scoringBehavior)
          // REVIEW: If nothing is thrown here 'createNode' will complain that
          // we return undefined
          throw new Error('Unknown scoring scoringBehavior')
        }
      }
    },
    expressionsInArgs: fn => [],
    typeChecker: isScore,
  }
}
